import axios, { AxiosInstance, AxiosError } from 'axios';

// When served via App Proxy, VITE_APP_BASENAME="/apps/rentals" so API calls go
// to /apps/rentals/api/* which Shopify proxies to our backend /proxy/api/*.
const basename = import.meta.env.VITE_APP_BASENAME || '';
const API_URL = import.meta.env.VITE_API_URL || `${basename}/api`;

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Get Shopify session token from App Bridge (available when embedded in Shopify Admin).
 * Returns null when running outside Shopify (e.g. clinic portal, standalone).
 */
async function getShopifySessionToken(): Promise<string | null> {
  try {
    const shopify = (window as any).shopify;
    if (shopify?.idToken) {
      return await shopify.idToken();
    }
  } catch {
    // Not in embedded context
  }
  return null;
}

// Request interceptor — inject auth tokens
api.interceptors.request.use(
  async (config) => {
    const clinicApiPath = import.meta.env.VITE_CLINIC_API_PREFIX || 'clinic-portal';
    if (config.url?.includes(clinicApiPath) && !import.meta.env.VITE_APP_BASENAME) {
      // Admin build only: attach JWT for clinic-portal routes
      const token = localStorage.getItem('clinic_auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } else if (!config.url?.includes(clinicApiPath) && !config.headers.Authorization) {
      // Admin routes: try Shopify session token from App Bridge
      const sessionToken = await getShopifySessionToken();
      if (sessionToken) {
        config.headers.Authorization = `Bearer ${sessionToken}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    // Handle common errors
    if (error.response?.status === 401) {
      const clinicApiPath = import.meta.env.VITE_CLINIC_API_PREFIX || 'clinic-portal';
      if (basename && error.config?.url?.includes(clinicApiPath)) {
        // App Proxy clinic-portal context: Shopify session expired/missing — redirect to storefront login
        window.location.href = `/account/login?return_url=${encodeURIComponent(basename)}`;
      } else if (error.config?.url?.includes(clinicApiPath) && !error.config?.url?.includes('/login')) {
        // Legacy JWT clinic portal: clear token and redirect to login page
        localStorage.removeItem('clinic_auth_token');
        localStorage.removeItem('clinic_auth_user');
        if (window.location.pathname.startsWith('/clinic') && window.location.pathname !== '/clinic/login') {
          window.location.href = '/clinic/login';
        }
      }
      console.error('Unauthorized access');
    }
    return Promise.reject(error);
  }
);

export default api;

// Helper types
export interface ApiError {
  message: string;
  statusCode: number;
  error?: string;
}

export function isApiError(error: unknown): error is AxiosError<ApiError> {
  return axios.isAxiosError(error);
}
